import React from 'react';
import { Toaster as HotToaster, ToastBar } from 'react-hot-toast';
import { WarningTriangle } from '@withjoy/joykit/icons';
import type { ToastContainerProps } from './Toast.types';
import { useLimit } from './useLimit';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { Box } from '../Box';
import { TOAST_DESTRUCTIVE_CLASS } from './Toast.constants';

export const ToastContainer: React.FC<ToastContainerProps> = ({ defaultPosition = 'top-center', defaultDuration = 3000, maxToastCount = 5 }) => {
  const [isMobile] = useResponsive<boolean>({ values: { mobile: true, tablet: false } });
  useLimit(maxToastCount);

  return (
    <HotToaster
      position={defaultPosition}
      toastOptions={{
        duration: defaultDuration,
      }}
    >
      {t => {
        // See Toast.constants: `intent: 'destructive'` arrives as a class on the toast object.
        const isDestructive = t.className === TOAST_DESTRUCTIVE_CLASS;
        // A destructive bar carries the warning mark by default, so the intent reads before the
        // sentence does. Anything the caller passed still wins — including an explicit `null`, which
        // is how the options type spells "no icon", so this tests for `undefined` not falsiness.
        //
        // No `size`: createSvgIcon's artwork sizes are 16/24/32/48/64, so an off-grid value renders
        // the next size down scaled up, and the ~234 hand-rolled `icon: <WarningTriangle />` toasts
        // already in the app all sit at the default 24. No `color` either — the Box below sets
        // `color="white"` and the icon paths use `currentColor`.
        const resolveIcon = () => {
          if (t.icon !== undefined) return t.icon;
          if (!isDestructive) return undefined;
          return <WarningTriangle />;
        };
        const icon = resolveIcon();

        return (
          <ToastBar
            toast={t}
            style={{
              boxShadow: 'none',
              padding: 0,
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: 'transparent',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '100%' : 480,
            }}
          >
            {() => (
              // Spread ariaProps (role="status" aria-live="polite" by default) so the
              // toast is announced to screen readers — our children render function
              // bypasses the ToastBar-built message node that normally carries them.
              <Box
                {...t.ariaProps}
                width="100%"
                typographyVariant="body1"
                paddingY={5}
                paddingX={6}
                alignItems="center"
                display="inline-flex"
                columnGap={5}
                color="white"
                backgroundColor={isDestructive ? 'negative6' : 'mono14'}
                textAlign={icon ? 'start' : 'center'}
                data-testid="toast-content"
              >
                {icon && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    data-testid="toast-icon"
                  >
                    {icon}
                  </Box>
                )}
                {t.message}
              </Box>
            )}
          </ToastBar>
        );
      }}
    </HotToaster>
  );
};
